import * as React from 'react';
import { connect } from 'react-redux';
import { lessonFlagOptions } from '../../../../constants/flagOptions';

import {
  DragHandle,
  QuestionSearch,
  SortableList,
  TextEditor,
  hashToCollection,
} from '../../index';
import { getQuestionConceptUID } from '../../libs/questionConceptUID';

export interface LessonFormProps {
  ChooseModelComponent: React.ComponentType<any>;
  concepts: any;
  conceptsFeedback: any;
  currentValues?: {
    flag?: string;
    introURL?: string;
    isELL?: boolean;
    landingPageHtml?: string;
    modelConceptUID?: string;
    name?: string;
    questions?: Array<{ key: string; questionType: string }>;
  };
  fillInBlank?: any;
  questions: any;
  showELL?: boolean;
  requiredQuestionType?: string;
  stateSpecificClass?: string;
  submit(data: {
    flag: string;
    isELL?: boolean;
    landingPageHtml: string;
    modelConceptUID: string;
    name: string;
    questions: Array<{ key: string; questionType: string }>;
  }): void;
  titleCards?: any;
}

export const LessonForm: React.FC<LessonFormProps> = ({
  ChooseModelComponent,
  concepts,
  conceptsFeedback,
  currentValues,
  fillInBlank,
  questions,
  showELL,
  stateSpecificClass,
  submit,
  titleCards,
  requiredQuestionType,
}) => {
  const { flag, introURL, isELL, landingPageHtml, modelConceptUID, name, questions: currentQuestions } = currentValues || {};

  const [formState, setFormState] = React.useState({
    flag: flag || 'alpha',
    introURL: introURL || '',
    isELL: isELL || false,
    landingPageHtml: landingPageHtml || '',
    modelConceptUID: modelConceptUID || null,
    name: name || '',
    questionType: requiredQuestionType || 'questions',
    selectedQuestions: currentQuestions || [],
  });

  const handleSubmit = () => {
    const { name, selectedQuestions, landingPageHtml, flag, modelConceptUID, isELL, questionType } = formState;
    submit({
      flag,
      isELL,
      landingPageHtml,
      modelConceptUID,
      name,
      questionType,
      questions: selectedQuestions,
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, name: e.target.value }));
  }

  const handleQuestionChange = (value: string) => {
    const { selectedQuestions, questionType } = formState;
    const changedQuestion = selectedQuestions.find(q => q.key === value);
    let newSelectedQuestions: any;

    if (!changedQuestion) {
      newSelectedQuestions = selectedQuestions.concat([{ key: value, questionType }]);
    } else {
      newSelectedQuestions = selectedQuestions.filter(question => question !== changedQuestion);
    }
    setFormState(prev => ({ ...prev, selectedQuestions: newSelectedQuestions }));
  };

  const handleSearchChange = ({ value }) => {
    handleQuestionChange(value);
  };

  const sortCallback = (sortInfo: any) => {
    const newOrder = sortInfo.map(item => {
      return { key: item.key, questionType: item.props.defaultValue };
    });
    setFormState(prev => ({ ...prev, selectedQuestions: newOrder }));
  };

  const renderQuestionSelect = () => {
    const { selectedQuestions } = formState;
    if (selectedQuestions && selectedQuestions.length) {
      const questionsList = selectedQuestions.map((question: { key: string; questionType: string }) => {
        const dataSource = { questions, fillInBlank, titleCards };
        const questionObject = dataSource[question.questionType].data[question.key];
        const prompt = questionObject ? questionObject.prompt : 'Question No Longer Exists';
        const promptOrTitle = questionObject && question.questionType === 'titleCards' ? questionObject.title : prompt;
        return (
          <div className="sortable-list-item" defaultValue={question.questionType} id={question.key} key={question.key}>
            <DragHandle><span className="focus-on-light sortable-list-item-prompt">{promptOrTitle}</span></DragHandle>
            <button onClick={() => handleQuestionChange(question.key)} type="button">Delete</button>
          </div>
        );
      });
      return <SortableList data={questionsList} id="currently-selected-questions" key={selectedQuestions.length} sortCallback={sortCallback} useDragHandle={true} />;
    } else {
      return <div id="currently-selected-questions">No questions</div>;
    }
  };

  const renderSearchBox = () => {
    const { questionType } = formState;
    const dataSource = { questions, fillInBlank, titleCards };
    let options = hashToCollection(dataSource[questionType].data);
    const conceptsData = concepts.data[0];

    if (!options.length) { return }
    
    if (questionType !== 'titleCards') {
      options = options.filter((option: { conceptID?: string; conceptUID?: string; concept_uid?: string; flag: string }) => {
        const concept = conceptsData.find(concept => concept.uid === getQuestionConceptUID(option));
        if (concept && option.flag !== 'archived') {
          return concept;
        }
      });
    }

    return (
      <QuestionSearch
        key={questionType}
        onChange={handleSearchChange}
        options={options}
        questionType={questionType}
      />
    );
  };

  const handleSelectFlag = (e: React.FocusEvent<HTMLSelectElement>) => {
    setFormState(prev => ({ ...prev, flag: e.currentTarget.value }));
  };

  const handleSelectQuestionType = (e: React.FocusEvent<HTMLSelectElement>) => {
    setFormState(prev => ({ ...prev, questionType: e.currentTarget.value }));
  };

  const onLandingPageChange = (e: string) => {
    setFormState(prev => ({ ...prev, landingPageHtml: e }));
  };

  const handleELLChange = () => {
    setFormState(prev => ({ ...prev, isELL: !prev.isELL }));
  };

  const onUpdateModelConcept = (uid: string) => {
    setFormState(prev => ({ ...prev, modelConceptUID: uid }));
  };

  return (
    <div className="box activity-form">
      <h4 className="title">Add New Activity</h4>
      <p className="control">
        <label className="label" htmlFor="name-input" id="name-label">
          Name
        </label>
        <input
          aria-labelledby="name-label"
          className="input"
          id="name-input"
          onChange={handleNameChange}
          placeholder="Text input"
          type="text"
          value={formState.name}
        />
      </p>
      <div className="control">
        <label className="label" htmlFor="landing-page-content">
          Landing Page Content
        </label>
        <TextEditor
          handleTextChange={onLandingPageChange}
          id="landing-page-content"
          text={formState.landingPageHtml || ''}
        />
      </div>
      <br />
      <p className="control">
        <label className="label" htmlFor="flag">
          Flag
          <span className="select">
            <select defaultValue={formState.flag} onBlur={handleSelectFlag}>
              {lessonFlagOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </span>
        </label>
      </p>
      {showELL && (
        <p className="control">
          <label className="label" htmlFor="ell-check-input">
            Is ELL?
            <span>
              <input
                aria-label="ell-check-input"
                checked={formState.isELL}
                onChange={handleELLChange}
                type="checkbox"
              />
            </span>
          </label>
        </p>
      )}
      {!requiredQuestionType && (
        <p className="control">
          <label className="label" htmlFor="question-type">
            Question Type
            <span className="select">
              <select defaultValue="questions" id="question-type" onBlur={handleSelectQuestionType}>
                <option value="questions">Sentence Combining</option>
                <option value="fillInBlank">Fill In the Blank</option>
                <option value="titleCards">Title Cards</option>
              </select>
            </span>
          </label>
        </p>
      )}
      <div className="control">
        <label className="label" htmlFor="currently-selected-questions">
          Currently Selected Questions -- {`Total: ${formState.selectedQuestions.length}`}
          {renderQuestionSelect()}
        </label>
      </div>
      {renderSearchBox()}
      <br />
      {conceptsFeedback && (
        <ChooseModelComponent
          conceptsFeedback={conceptsFeedback}
          modelConceptUID={formState.modelConceptUID}
          updateModelConcept={onUpdateModelConcept}
        />
      )}
      <p className="control">
        <button className={`button is-primary ${stateSpecificClass}`} onClick={handleSubmit} type="submit">
          Submit
        </button>
      </p>
    </div>
  );
};

function select({ questions, concepts, conceptsFeedback, fillInBlank, titleCards }) {
  return {
    concepts,
    conceptsFeedback,
    fillInBlank,
    questions,
    titleCards,
  };
}

export default connect(select)(LessonForm);
